import { useEffect, useState } from 'react';
import { Plus, Shield, MoreHorizontal, Pencil, Trash2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  ConfirmDialog,
} from '@/components/common';
import { rolesApi, Role } from '@/api/services';
import { getErrorMessage } from '@/api/client';

export default function Roles() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; role?: Role }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await rolesApi.getAll();
      setRoles(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleDelete = async () => {
    if (!deleteDialog.role) return;

    try {
      setDeleting(true);
      await rolesApi.delete(deleteDialog.role.id);
      toast.success('Role deleted successfully');
      setDeleteDialog({ open: false });
      fetchRoles();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<Role>[] = [
    {
      key: 'name',
      header: 'Role',
      sortable: true,
      render: (role) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10">
            <Shield className="h-4 w-4 text-accent" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium">{role.name}</span>
              {role.isSystem && (
                <Badge variant="secondary" className="text-xs">
                  <Lock className="mr-1 h-3 w-3" />
                  System
                </Badge>
              )}
            </div>
            <div className="text-sm text-muted-foreground">{role.code}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (role) => (
        <span className="text-muted-foreground">
          {role.description || '-'}
        </span>
      ),
    },
    {
      key: 'accessObjects',
      header: 'Permissions',
      render: (role) => (
        <Badge variant="outline">
          {role.accessObjects.length} permission{role.accessObjects.length !== 1 ? 's' : ''}
        </Badge>
      ),
    },
    {
      key: '_count',
      header: 'Users',
      render: (role) => role._count?.userRoles || 0,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (role) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={role.isSystem}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              disabled={role.isSystem}
              onClick={() => !role.isSystem && setDeleteDialog({ open: true, role })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="p-6">
      <PageHeader
        title="Roles & Permissions"
        description="Manage user roles and their permissions"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Roles' },
        ]}
        actions={
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create Role
          </Button>
        }
      />

      <DataTable
        data={roles}
        columns={columns}
        loading={loading}
        emptyTitle="No roles found"
        emptyDescription="Create custom roles to manage user permissions."
        emptyAction={
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create Role
          </Button>
        }
      />

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Delete Role"
        description={`Are you sure you want to delete the "${deleteDialog.role?.name}" role? Users with this role will lose their permissions.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleting}
      />
    </div>
  );
}
